import React, { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AccordionTableDesc,
  ExpandContainer,
  AccordionBody,
  TileCont,
  TileBody,
  TileSide,
  ScoreResult,
  TileTitle,
  ChartCont,
} from './CategoriesAndTopicsTableTile.styles';
import CategoriesAndTopicsTileAccordionEl from './CategoriesAndTopicsTileAccordionEl/CategoriesAndTopicsTileAccordionEl';
import { getLocaleFormatDate } from '../../../helpers/helpers';
import { useFilters } from '../../../hooks/useFilters';
import { useLocalSort } from '../../../hooks/useLocalSort';
import { ICategoryResult, ITopicResults } from '../../../services/reports';
import LittleWormChart from '../../LittleWormChart/LittleWormChart';
import ProgressBar from '../../ProgressBar/ProgressBar';
import SafeHtml from '../../SafeHtml/SafeHtml';
import ScoreTrend from '../../ScoreTrend/ScoreTrend';
import CategoriesAndTopicsTileAccordionHeader from './CategoriesAndTopicsTileAccordionHeader/CategoriesAndTopicsTileAccordionHeader'; // eslint-disable-line
import ExpandArrowClose from '@/assets/icons/closeAllAccordion.svg?react';
import ExpandArrowOpen from '@/assets/icons/openAllAccordion.svg?react';

interface ICategoriesAndTopicsTableTile {
  item: ICategoryResult;
  isTotalsTabActive: boolean;
}

const CategoriesAndTopicsTableTile: FC<ICategoriesAndTopicsTableTile> = ({
  item,
  isTotalsTabActive,
}) => {
  const { i18n } = useTranslation();
  const reportKey = 'surveyReport';
  const { topicResults, category, score, scoreRating, teamDistribution, benchmarks } = item;
  const { code, title } = category;
  const { sortItems, loading, getThisNestSortFilters } = useLocalSort(
    [...topicResults],
    reportKey,
    item.category.code
  );
  const id = code || '1';
  const { getNestedFilters, applyNestedFilter } = useFilters();
  const reportTileFilters = getNestedFilters(reportKey);
  const active = reportTileFilters[id]?.active || false;
  const sortOrderObj = reportTileFilters[id]?.sortOrder;

  const { historical, survey } = benchmarks ?? {};

  const renderAccordion = useMemo(() => {
    return sortItems?.map((topic: ITopicResults) => (
      <CategoriesAndTopicsTileAccordionEl
        parentCode={code}
        key={topic?.topic?.code}
        item={topic}
        reportKey={reportKey}
        isTotalsTabActive={isTotalsTabActive}
      />
    ));
  }, [sortItems, code, sortOrderObj, isTotalsTabActive]);

  return (
    <TileCont id={code}>
      <TileBody id="TileBody" onClick={() => applyNestedFilter({ active: !active }, id, reportKey)}>
        <TileSide id="TileSide" isTotalsTabActive={isTotalsTabActive}>
          <ExpandContainer
            id="ExpandContainer"
            onClick={() => applyNestedFilter({ active: !active }, id, reportKey)}
          >
            {!active ? <ExpandArrowOpen /> : <ExpandArrowClose />}
          </ExpandContainer>
          <TileTitle id="TileTitle">{title}</TileTitle>
          {isTotalsTabActive && (
            <ScoreResult id="CategoryScore">
              <span>{score}</span>
              {historical ? (
                <ScoreTrend
                  progress={historical?.change}
                  stateType={historical?.changeDirectionality}
                />
              ) : null}
            </ScoreResult>
          )}
        </TileSide>
        <ChartCont id="ChartCont" isTotalsTabActive={isTotalsTabActive}>
          {isTotalsTabActive ? (
            <ProgressBar
              barMode={scoreRating}
              contWidth="100%"
              height="18px"
              historicalScore={historical?.score}
              progress={`${score}%`}
              tooltip={`${getLocaleFormatDate(survey?.endDate ?? '', i18n.language)} ${
                survey?.type
              }`}
            />
          ) : (
            teamDistribution && (
              <LittleWormChart
                weakValue={teamDistribution.weak}
                normalValue={teamDistribution.normal}
                strongValue={teamDistribution.strong}
                size="small"
              />
            )
          )}
        </ChartCont>
      </TileBody>
      {active && (
        <AccordionBody id="AccordionBody">
          <AccordionTableDesc id="AccordionTableDesc">
            <SafeHtml html={category.description} />
          </AccordionTableDesc>
          <CategoriesAndTopicsTileAccordionHeader
            code={code}
            sortFilters={getThisNestSortFilters}
            isTotalsTabActive={isTotalsTabActive}
          />
          {loading ? <div>loading</div> : renderAccordion}
        </AccordionBody>
      )}
    </TileCont>
  );
};

export default CategoriesAndTopicsTableTile;
